<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useBuildsStore } from '../stores/builds';
import { useAuthStore } from '../stores/auth';
import { useOrganizationsStore } from '../stores/organizations';
import { usePreferencesStore } from '../stores/preferences';
import BuildList from '../components/BuildList.vue';
import NewBuildModal from '../components/NewBuildModal.vue';
import type { UIRefType } from '../types';

const router = useRouter();
const route = useRoute();
const buildsStore = useBuildsStore();
const authStore = useAuthStore();
const organizationsStore = useOrganizationsStore();
const preferencesStore = usePreferencesStore();

// Org-scoped variant: /:orgSlug renders this same view filtered to one org
const orgSlug = computed(() => (route.params.orgSlug as string) || undefined);
const scopedOrgName = ref<string | null>(null);

// Filter state (persisted via preferences store)
const statusFilter = computed({
  get: () => preferencesStore.buildsStatusFilter,
  set: (value: string) => preferencesStore.setBuildsStatusFilter(value),
});
const statuses = ['', 'pending', 'running', 'success', 'failed', 'cancelling', 'cancelled'] as const;
const mineOnly = computed({
  get: () => preferencesStore.buildsMineOnly,
  set: (value: boolean) => preferencesStore.setBuildsMineOnly(value),
});

// Check for pre-fill params from query string
const prefillRepositoryId = computed(() => (route.query.repositoryId as string) || '');
const prefillBranch = computed(() => (route.query.branch as string) || '');
const prefillPipeline = computed(() => (route.query.pipeline as string) || '');
const prefillRefType = computed(() => (route.query.refType as UIRefType) || undefined);

// Auto-show modal if prefill params are present
const showModal = ref(!!route.query.repositoryId);

function loadBuilds(page = 1) {
  buildsStore.fetchBuilds({
    page,
    status: statusFilter.value || undefined,
    organization: orgSlug.value,
    mine: mineOnly.value,
  });
}

// Watch for filter changes
watch([statusFilter, mineOnly], () => loadBuilds());

onMounted(async () => {
  if (orgSlug.value) {
    const org = await organizationsStore.fetchOrganization(orgSlug.value);
    scopedOrgName.value = org?.name ?? orgSlug.value;
  }
  loadBuilds();
});

function getStatusLabel(status: string): string {
  return status || 'All';
}

async function handleCreateBuild(data: { repositoryId: string; branch: string; pipeline?: string; refType?: 'branch' | 'tag' | 'pull_request' }) {
  const build = await buildsStore.createBuild({
    repositoryId: data.repositoryId,
    branch: data.branch || undefined,
    pipeline: data.pipeline,
    refType: data.refType,
  });
  if (build) {
    showModal.value = false;
    // Clear query params and navigate to build detail
    router.push(`/builds/${build.id}`);
  }
}

function handleCloseModal() {
  showModal.value = false;
  // Clear query params if present
  if (route.query.repositoryId) {
    router.replace({ query: {} });
  }
}
</script>

<template>
  <div class="builds-view">
    <div class="header">
      <h1>
        <template v-if="orgSlug">{{ scopedOrgName ?? orgSlug }} <span class="header-sub">builds</span></template>
        <template v-else>Builds</template>
      </h1>
      <button
        v-if="authStore.canWrite"
        @click="showModal = true"
        class="btn-primary"
      >
        New Build
      </button>
    </div>

    <div class="filters">
      <span class="filter-label">Status:</span>
      <div class="filter-buttons">
        <button
          v-for="status in statuses"
          :key="status"
          :class="['filter-btn', { active: statusFilter === status }]"
          @click="statusFilter = status"
        >
          {{ getStatusLabel(status) }}
        </button>
      </div>
      <button
        :class="['filter-btn', 'mine-toggle', { active: mineOnly }]"
        :aria-pressed="mineOnly"
        title="Builds of commits and pull requests you authored, and builds you triggered"
        @click="mineOnly = !mineOnly"
      >
        My builds
      </button>
    </div>

    <!-- New Build Modal -->
    <NewBuildModal
      v-if="showModal"
      :initial-repository-id="prefillRepositoryId"
      :initial-branch="prefillBranch"
      :initial-pipeline="prefillPipeline"
      :initial-ref-type="prefillRefType"
      @submit="handleCreateBuild"
      @close="handleCloseModal"
    />

    <div v-if="buildsStore.error" class="error">
      {{ buildsStore.error }}
    </div>

    <div v-if="buildsStore.loading" class="loading">
      Loading...
    </div>

    <BuildList v-else :builds="buildsStore.builds" />

    <div v-if="buildsStore.total > buildsStore.pageSize" class="pagination">
      <button
        :disabled="buildsStore.page <= 1"
        @click="loadBuilds(buildsStore.page - 1)"
      >
        Previous
      </button>
      <span>Page {{ buildsStore.page }} of {{ Math.ceil(buildsStore.total / buildsStore.pageSize) }}</span>
      <button
        :disabled="buildsStore.page >= Math.ceil(buildsStore.total / buildsStore.pageSize)"
        @click="loadBuilds(buildsStore.page + 1)"
      >
        Next
      </button>
    </div>
  </div>
</template>

<style scoped>
.builds-view {
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

h1 {
  font-size: 1.75rem;
  margin: 0;
  color: var(--color-text-primary);
}

.header-sub {
  font-weight: 400;
  color: var(--color-text-secondary);
}

.btn-primary {
  padding: 10px 20px;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: var(--color-btn-primary-hover);
}

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.filter-label {
  font-weight: 500;
  color: var(--color-text-secondary);
}

.filter-buttons {
  display: flex;
  gap: 4px;
}

.filter-btn {
  padding: 6px 12px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-primary);
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: capitalize;
  color: var(--color-text-primary);
}

.filter-btn:hover {
  border-color: var(--color-accent);
}

.mine-toggle {
  margin-left: auto;
  text-transform: none;
}

.filter-btn.active {
  background: var(--color-accent);
  color: var(--color-btn-primary-text);
  border-color: var(--color-accent);
}

.error {
  background: var(--color-error-bg);
  color: var(--color-error-text);
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  color: var(--color-text-primary);
}

.pagination button {
  padding: 8px 16px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-primary);
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text-primary);
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tablet breakpoint (768px) */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-buttons {
    flex-wrap: wrap;
    gap: 8px;
  }

  .filter-btn {
    min-height: 44px;
    padding: 10px 14px;
  }

  .mine-toggle {
    margin-left: 0;
  }
}

/* Phone breakpoint (480px) */
@media (max-width: 480px) {
  .builds-view {
    padding: 0;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .btn-primary {
    width: 100%;
    min-height: 44px;
  }

  h1 {
    font-size: 1.5rem;
  }

  .filter-buttons {
    width: 100%;
  }

  .filter-btn {
    flex: 1 1 calc(33.33% - 8px);
    text-align: center;
    min-width: 0;
  }

  .pagination {
    flex-wrap: wrap;
    gap: 8px;
  }

  .pagination button {
    min-height: 44px;
  }
}
</style>
